import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, Flex, InputNumber } from "antd";
import dayjs from "dayjs";
import { useContext, useEffect, useState } from "react";
import { IndexContext } from "../../../context/IndexContext";
import calculateChange from "../../../functions/Common/calculateChange";
import { useShowNotification } from "../../../functions/Common/showNotification";
import useGetMerchantId from "../../../functions/Merchant/getMerchantId";
import useGetNewInvoiceNo from "../../../functions/Transaction/getNewInvoiceNo";
import FunctionalButton from "../../Common/FunctionalButton";
import Receipt from "./Receipt";

export default function Cash(props) {
  const {
    plates,
    onPaymentSuccessReset,
    receiptPrice,
    setReceiptPrice,
    transactionInfo,
    setTransactionInfo,
    setExtraChargesOptions,
  } = props;

  const showNotification = useShowNotification();
  const { merchantId } = useGetMerchantId(receiptPrice);
  const { invoiceNo } = useGetNewInvoiceNo(receiptPrice);

  const [cashValue, setCashValue] = useState("0");
  const [paymentConfirm, setPaymentConfirm] = useState(false);
  const [
    isDisableCompleteTransactionButton,
    setIsDisableCompleteTransactionButton,
  ] = useState(true);

  const confirmPayment = () => {
    const numberCashValue = parseFloat(cashValue || "0");
    if (numberCashValue < receiptPrice.total) {
      showNotification({
        message: "Insufficient Cash Payment",
        description:
          "The cash amount entered is less than the total amount. Please provide the full payment to proceed",
      });
    } else {
      setReceiptPrice((prev) => ({ ...prev, cash: numberCashValue }));
      setPaymentConfirm(true);
    }
  };

  const paymentReset = () => {
    setPaymentConfirm(false);
    onPaymentSuccessReset();
  };

  const handleNumpadInput = (value, isDenominator) => {
    if (isDenominator) {
      setCashValue(value.toString());
      return;
    }

    const currentValue = cashValue?.toString() ?? "0";
    let newValue = currentValue;

    if (value === "back") {
      newValue = currentValue.slice(0, -1) || "0";
    } else if (value === ".") {
      if (!currentValue.includes(".")) {
        newValue = currentValue + ".";
      }
    } else {
      // Only allow up to 2 decimal digits
      if (currentValue.includes(".")) {
        const [, decimal] = currentValue.split(".");
        if (decimal.length >= 2) return; // prevent more than 2 decimals
      }

      if (currentValue === "0") {
        newValue = value.toString();
      } else {
        newValue = currentValue + value.toString();
      }
    }
    setCashValue(newValue);
  };

  useEffect(() => {
    if (receiptPrice.cash != null) {
      let amount = calculateChange(receiptPrice);
      setReceiptPrice((prev) => ({
        ...prev,
        total: amount.total,
        change: amount.change,
        round_adj: amount.round_adj,
      }));
    }
  }, [receiptPrice?.cash]);

  useEffect(() => {
    if (invoiceNo && merchantId) {
      setIsDisableCompleteTransactionButton(false);
      setTransactionInfo({
        invoice_no: invoiceNo,
        payment_id: null,
        amount: receiptPrice.total,
        sst_amount: receiptPrice.sst,
        subtotal: receiptPrice.subtotal,
        transaction_datetime: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        payment_method: "Cash",
        status: 1,
        merchant_id: merchantId,
        pg_transaction_id: null,
        signature: null,
        err_desc: null,
        pay_amount: receiptPrice.cash,
        change_amount: receiptPrice.change,
        round_adj_amount: receiptPrice.round_adj,
        takeaway_fee: receiptPrice.takeaway_fee,
      });
    }
  }, [invoiceNo, merchantId]);

  return (
    <div
      style={{
        height: "100%",
        width: "100%",
        overflowX: "hidden",
        overflowY: "auto",
      }}
    >
      {paymentConfirm ? (
        <Receipt
          plates={plates}
          onPaymentSuccessReset={paymentReset}
          transactionInfo={transactionInfo}
          setTransactionInfo={setTransactionInfo}
          isDisableCompleteTransactionButton={
            isDisableCompleteTransactionButton
          }
          setExtraChargesOptions={setExtraChargesOptions}
        />
      ) : (
        <Flex
          gap={"middle"}
          vertical="vertical"
          style={{ width: "100%", height: "100%" }}
        >
          <InputNumber
            className="cash-input-number"
            autoFocus={true}
            min={0}
            size="large"
            value={`RM ${cashValue}`}
            readOnly
            style={{ height: "30%", textAlign: "center" }}
          />
          <CustomNumPad handleNumpadInput={handleNumpadInput} />
          <Flex gap={"middle"} style={{ height: "20%" }}>
            <FunctionalButton
              width="30%"
              danger
              children={<strong style={{ fontSize: "24px" }}>Reset</strong>}
              buttonClickFunction={() => setCashValue("0")}
            />
            <FunctionalButton
              children={
                <strong style={{ fontSize: "24px" }}>Confirm Payment</strong>
              }
              width="70%"
              background="#38B865"
              buttonClickFunction={confirmPayment}
            />
          </Flex>
        </Flex>
      )}
    </div>
  );
}

function CustomNumPad({ handleNumpadInput }) {
  const { isLightTheme } = useContext(IndexContext);

  const DENOMINATIONS = [10, 15, 20, 50];
  const NUMPAD_FORMAT = [
    [1, 2, 3],
    [4, 5, 6],
    [7, 8, 9],
    [".", 0, "back"],
  ];

  return (
    <Flex gap={16} style={{ height: "100%" }}>
      <div style={{ flex: "0 0 30%", height: "100%" }}>
        <Flex vertical gap="middle" style={{ height: "100%" }}>
          {DENOMINATIONS.map((amount) => (
            <Button
              key={amount}
              type="primary"
              style={{
                width: "100%",
                height: "100%",
                fontSize: "40px",
                fontWeight: "bold",
                borderRadius: "8px",
              }}
              onClick={() => handleNumpadInput(amount, true)}
            >
              RM {amount}
            </Button>
          ))}
        </Flex>
      </div>

      <div style={{ flex: 1, height: "100%" }}>
        <Flex vertical gap="middle" style={{ height: "100%" }}>
          {NUMPAD_FORMAT.map((row, rowIndex) => (
            <Flex
              key={rowIndex}
              gap={12}
              style={{ height: "25%" }}
              align="center"
            >
              {row.map((digit, colIndex) => (
                <div
                  key={colIndex}
                  style={{
                    flex: 1,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {digit != null && (
                    <Button
                      style={{
                        width: "100%",
                        height: "100%",
                        fontSize: "40px",
                        fontWeight: "bold",
                        borderRadius: "8px",
                        boxShadow: "0 2px 0 rgba(0, 0, 0, 0.02)",
                        background: isLightTheme ? "#E9EAEC" : "#606060",
                      }}
                      onClick={() => handleNumpadInput(digit, false)}
                    >
                      {digit === "back" ? <ArrowLeftOutlined /> : digit}
                    </Button>
                  )}
                </div>
              ))}
            </Flex>
          ))}
        </Flex>
      </div>
    </Flex>
  );
}
